import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { useLanguage } from '../context/LanguageContext';
import { useJobs } from '../hooks/useJobs';
import { supabase, supabaseConfigError, Job } from '../lib/supabase';
import './JobDetailPage.css';
import { formatSalaryDisplay, parseSalary } from '../utils/salary';
import SocialShare from './SocialShare';
import ApplicationForm from './ApplicationForm';

const JobDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { language } = useLanguage();
  const { jobs } = useJobs();

  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const jobId = job?.id ?? null;

  useEffect(() => {
    if (!id) return;
    if (supabaseConfigError || !supabase) {
      setError('De vacature kan momenteel niet worden geladen.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setJob(null);
    supabase
      .from('jobs')
      .select('*')
      .eq('id', id)
      .single()
      .then(({ data, error: fetchError }) => {
        if (fetchError) {
          setError('De vacature kon niet worden gevonden.');
        } else {
          setJob(data);
        }
        setLoading(false);
      });
  }, [id]);

  const BOOKMARK_KEY = 'mk_bookmarked_job_ids';
  const [bookmarkedIds, setBookmarkedIds] = useState<string[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const stored = localStorage.getItem(BOOKMARK_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const isBookmarked = jobId ? bookmarkedIds.includes(jobId) : false;

  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [applicationSuccess, setApplicationSuccess] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(BOOKMARK_KEY, JSON.stringify(bookmarkedIds));
    } catch {
      // ignore storage errors
    }
  }, [bookmarkedIds]);

  // Scroll to top when job ID changes (e.g., when clicking related jobs)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'nl' ? 'nl-NL' : 'en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const description = useMemo(() => (language === 'nl' ? job?.description_nl : job?.description_en) || '', [language, job]);
  const plainDescription = useMemo(() => {
    if (!description) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = description;
    return tmp.textContent || tmp.innerText || '';
  }, [description]);

  useEffect(() => {
    if (!job) return;
    const pageTitle = `${language === 'nl' ? job.title_nl : job.title_en} | Meijer & Knijnenberg`;
    document.title = pageTitle;

    const setMeta = (name: string, content: string) => {
      let tag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!tag) {
        tag = document.createElement('meta');
        tag.name = name;
        document.head.appendChild(tag);
      }
      tag.content = content;
    };

    const setMetaProperty = (property: string, content: string) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    };

    const descriptionText = plainDescription.slice(0, 160);
    const currentUrl = window.location.href;
    // Use the dedicated LinkedIn social card image for job shares
    const imageUrl = 'https://www.meijerknijnenberg.com/assets/logos/mk-linkedin-card-v2.png?v=1';

    setMeta('description', descriptionText);

    // Open Graph tags for LinkedIn/Facebook
    setMetaProperty('og:type', 'article');
    setMetaProperty('og:url', currentUrl);
    setMetaProperty('og:title', pageTitle);
    setMetaProperty('og:description', descriptionText);
    setMetaProperty('og:image', imageUrl);
    setMetaProperty('og:image:width', '1152');
    setMetaProperty('og:image:height', '588');
    setMetaProperty('og:site_name', 'Meijer & Knijnenberg');

    // Twitter Card tags
    setMetaProperty('twitter:card', 'summary_large_image');
    setMetaProperty('twitter:url', currentUrl);
    setMetaProperty('twitter:title', pageTitle);
    setMetaProperty('twitter:description', descriptionText);
    setMetaProperty('twitter:image', imageUrl);

    const scriptId = 'jobposting-jsonld';
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();

    const [minSalary, maxSalary] = parseSalary(job.salary);
    const salaryParsable = !(minSalary === 0 && maxSalary === 999999);

    const jsonLd: Record<string, any> = {
      '@context': 'https://schema.org',
      '@type': 'JobPosting',
      title: language === 'nl' ? job.title_nl : job.title_en,
      description: plainDescription,
      datePosted: job.posted_date,
      validThrough: job.deadline_date || undefined,
      employmentType: job.type,
      hiringOrganization: {
        '@type': 'Organization',
        name: 'Meijer & Knijnenberg',
        sameAs: 'https://meijerknijnenberg.com'
      },
      jobLocation: {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          addressLocality: job.location,
          addressCountry: 'NL'
        }
      },
      identifier: {
        '@type': 'PropertyValue',
        name: 'Meijer & Knijnenberg',
        value: job.id
      },
      directApply: false,
      url: window.location.href
    };

    if (salaryParsable) {
      jsonLd.baseSalary = {
        '@type': 'MonetaryAmount',
        currency: 'EUR',
        value: {
          '@type': 'QuantitativeValue',
          minValue: minSalary,
          maxValue: maxSalary,
          unitText: 'YEAR'
        }
      };
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.innerHTML = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      const cleanup = document.getElementById(scriptId);
      if (cleanup) cleanup.remove();
    };
  }, [job, language, plainDescription]);

  if (loading) {
    return (
      <div className="job-detail-page">
        <div className="job-not-found">
          <h1>{language === 'nl' ? 'Vacature laden...' : 'Loading job...'}</h1>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="job-detail-page">
        <div className="job-not-found">
          <h1>{language === 'nl' ? 'Configuratiefout' : 'Configuration error'}</h1>
          <p>{error}</p>
          <Link to="/jobs" className="back-to-jobs">
            {language === 'nl' ? '← Terug naar vacatures' : '← Back to jobs'}
          </Link>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="job-detail-page">
        <div className="job-not-found">
          <h1>{language === 'nl' ? 'Vacature niet gevonden' : 'Job not found'}</h1>
          <p>{language === 'nl' 
            ? 'De gevraagde vacature kon niet worden gevonden.' 
            : 'The requested job could not be found.'}
          </p>
          <Link to="/jobs" className="back-to-jobs">
            {language === 'nl' ? '← Terug naar vacatures' : '← Back to jobs'}
          </Link>
        </div>
      </div>
    );
  }

  const title = language === 'nl' ? job.title_nl : job.title_en;
  const salaryText = formatSalaryDisplay(job.salary, language === 'nl' ? 'nl' : 'en');
  const toggleBookmark = () => {
    if (!jobId) return;
    setBookmarkedIds((prev) =>
      prev.includes(jobId) ? prev.filter((pid) => pid !== jobId) : [...prev, jobId]
    );
  };

  return (
    <div className="job-detail-page">
      <div className="job-detail-breadcrumb">
        <Link to="/">{language === 'nl' ? 'Home' : 'Home'}</Link>
        <span>›</span>
        <Link to="/jobs">{language === 'nl' ? 'Vacatures' : 'Jobs'}</Link>
        <span>›</span>
        <span>{title}</span>
      </div>

      <div className="job-detail-container">
        <div className="job-detail-main">
          <div className="job-detail-header">
            <div className="job-detail-header-content">
              {job.image_url && (
                <div className="job-detail-header-image">
                  <img src={job.image_url} alt={title} loading="lazy" />
                </div>
              )}
              <div className="job-detail-header-text">
                <h1 className="job-detail-title">{title}</h1>
                <div className="job-detail-meta">
                  <span className="job-detail-meta-item">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="7" width="18" height="14" rx="2" ry="2"/>
                      <path d="M8 5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2H8V5z"/>
                    </svg>
                    {job.discipline}
                  </span>
                  <span className="job-detail-meta-item">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                    {job.location}
                  </span>
                  <span className="job-detail-meta-item">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 8c-2.21 0-4 .895-4 2s1.79 2 4 2 4 .895 4 2-1.79 2-4 2-4-.895-4-2"/>
                      <path d="M12 6v2m0 8v2m8-6c0 3.314-3.582 6-8 6s-8-2.686-8-6 3.582-6 8-6 8 2.686 8 6z"/>
                    </svg>
                    {salaryText}
                  </span>
                  <span className="job-detail-meta-item">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <polyline points="12 6 12 12 16 14"/>
                    </svg>
                    {formatDate(job.posted_date)}
                  </span>
                </div>
              </div>
              <div className="job-detail-header-actions">
                <button
                  className="apply-now-button"
                  onClick={() => setShowApplicationForm(true)}
                >
                  {language === 'nl' ? 'Solliciteer nu' : 'Apply Now'}
                </button>
                <button
                  className={`bookmark-button ${isBookmarked ? 'active' : ''}`}
                  onClick={toggleBookmark}
                  aria-pressed={isBookmarked}
                  aria-label={isBookmarked
                    ? (language === 'nl' ? 'Verwijder bookmark' : 'Remove bookmark')
                    : (language === 'nl' ? 'Bewaar vacature' : 'Save job')}
                  title={isBookmarked
                    ? (language === 'nl' ? 'Verwijder bookmark' : 'Remove bookmark')
                    : (language === 'nl' ? 'Bewaar vacature' : 'Save job')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <div className="job-detail-content">
            <div
              className="job-description-content"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(description, {
                  ALLOWED_TAGS: ['p', 'strong', 'em', 'h3', 'h4', 'ul', 'ol', 'li', 'br', 'a'],
                  ALLOWED_ATTR: ['href', 'target', 'rel']
                })
              }}
            />

            <div className="job-detail-actions" style={{ marginTop: '2rem', display: 'flex', alignItems: 'flex-end', gap: '1.5rem', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.9rem', fontWeight: '600', color: 'var(--mk-navy)', marginBottom: '1rem' }}>
                  {language === 'nl' ? 'Deel deze vacature:' : 'Share this job:'}
                </span>
                <SocialShare
                  url={window.location.href}
                  title={title}
                  description={plainDescription.slice(0, 160)}
                />
              </div>
            </div>
          </div>
        </div>

        <aside className="job-detail-sidebar">
          <div className="job-sidebar-card">
            <h3>{language === 'nl' ? 'Vacature overzicht' : 'Job Overview'}</h3>
            <div className="job-overview-item">
              <strong>{language === 'nl' ? 'Geplaatst op' : 'Date Posted'}</strong>
              <span>{formatDate(job.posted_date)}</span>
            </div>
            <div className="job-overview-item">
              <strong>{language === 'nl' ? 'Locatie' : 'Location'}</strong>
              <span>{job.location}</span>
            </div>
            <div className="job-overview-item">
              <strong>{language === 'nl' ? 'Salaris' : 'Salary'}</strong>
              <span>{salaryText}</span>
            </div>
            <div className="job-overview-item">
              <strong>{language === 'nl' ? 'Type' : 'Job Type'}</strong>
              <span>{job.type}</span>
            </div>
            {job.deadline_date && (
              <div className="job-overview-item">
                <strong>{language === 'nl' ? 'Sluitingsdatum' : 'Deadline'}</strong>
                <span>{formatDate(job.deadline_date)}</span>
              </div>
            )}
          </div>

          <div className="job-sidebar-card">
            <h3>{language === 'nl' ? 'Contact' : 'Contact'}</h3>
            <div className="contact-info">
              <a href="tel:+31203086271" className="contact-link">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
                </svg>
                020 308 6271
              </a>
              <div className="contact-address">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                <div>
                  <div>Nieuwezijds voorburgwal 334-2</div>
                  <div>Amsterdam</div>
                </div>
              </div>
              <a href="mailto:info@meijerknijnenberg.com" className="contact-link">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                info@meijerknijnenberg.com
              </a>
            </div>
          </div>

          <div className="job-sidebar-card job-location-map">
            <h3>{language === 'nl' ? 'Locatie' : 'Job Location'}</h3>
            <div className="map-placeholder">
              <iframe
                src={`https://maps.google.com/maps?q=${encodeURIComponent(job.location + ', Netherlands')}&t=&z=13&ie=UTF8&iwloc=&output=embed`}
                width="100%"
                height="200"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                title={`Job location: ${job.location}`}
              ></iframe>
            </div>
          </div>
        </aside>
      </div>

      {jobs.length > 1 && (
        <div className="related-jobs">
          <h2>{language === 'nl' ? 'Gerelateerde vacatures' : 'Related Jobs'}</h2>
          <div className="related-jobs-grid">
            {jobs
              .filter(j => j.id !== job.id && j.discipline === job.discipline)
              .slice(0, 3)
              .map(relatedJob => (
                <Link 
                  key={relatedJob.id} 
                  to={`/jobs/${relatedJob.id}`}
                  className="related-job-card"
                >
                  <h3>{language === 'nl' ? relatedJob.title_nl : relatedJob.title_en}</h3>
                  <div className="related-job-meta">
                    <span>{relatedJob.discipline}</span>
                    <span>{relatedJob.location}</span>
                    <span>{formatSalaryDisplay(relatedJob.salary, language === 'nl' ? 'nl' : 'en')}</span>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      )}

      {showApplicationForm && job && (
        <ApplicationForm
          jobId={job.id!}
          jobTitle={language === 'nl' ? job.title_nl : job.title_en}
          uploaderEmail={job.uploader_email}
          language={language}
          onClose={() => setShowApplicationForm(false)}
          onSuccess={() => {
            setShowApplicationForm(false);
            setApplicationSuccess(true);
            setTimeout(() => setApplicationSuccess(false), 5000);
          }}
        />
      )}

      {applicationSuccess && (
        <div className="application-success-banner">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="9,12 12,15 15,9" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>
            {language === 'nl'
              ? 'Bedankt voor je sollicitatie! We nemen zo snel mogelijk contact met je op.'
              : 'Thank you for your application! We will contact you as soon as possible.'}
          </span>
        </div>
      )}
    </div>
  );
};

export default JobDetailPage;
